import { renderCategoryLink, renderCategoryWrapper, renderCategoriesContainer, renderServiceItem } from '../utils/templates.js';
import { shout } from "../utils/shout.js";
import {isNonNullObject} from "../utils/helpers.js";

export default class ServicesStep {
  constructor() {
    this.id = "services";
    this.title_key = "pick_services";
  }

  init({ $, state, data, emit }) {
    this.$ = $;
    this.state = state;
    this.data = data;
    this.emit = emit;
    this.selected = new Set(state.selectedServices || []);

    this.employeesFirst = state.stepContext?.employeesFirst;  // Check if we're in "employees-first" mode
    this.maxServices = Math.max(1, data.config.max_services);

    if(isNonNullObject(state.selectedSubscription)) {
      this.subscription = state.selectedSubscription;
      const subType = this.subscription?.type || 'none';
      switch (subType) {
        case 'none':
          break;
        case 'single':
          this.maxServices = 1;
          break;
        case 'group':
          shout('Subscription with group detected in services.');
          break;
        default:
          shout('Unknown subscription type.');
          break;
      }
    }

    // Transform only once
    this.employeeToServicesMap = this.transformData(data.filteredServicesIds);
    this.availableServices = this.getAvailableServices([...this.selected]);
  }

  transformData(input) {
    const output = {};

    for (let employee in input) {
      output[employee] = {};
      for (let category in input[employee]) {
        for (let service of input[employee][category]) {
          if (!output[employee][service]) {
            output[employee][service] = [];
          }
          output[employee][service].push(category);
        }
      }
    }

    return output;
  }

  getAvailableServices(selectedServices) {
    const validEmployees = [];

    if (this.employeesFirst) {
      // When "employees-first" mode is enabled, show all services for the selected employee
      const employeeId = this.state.selectedEmployee; // Get the employee ID from state

      if (employeeId) {
        // Get all services for the selected employee
        return Object.keys(this.employeeToServicesMap[employeeId] || {});
      } else {
        return [];
      }
    } else {
      // Original behavior: services depend on employee's capabilities
      for (let employeeId in this.employeeToServicesMap) {
        const servicesMap = this.employeeToServicesMap[employeeId];

        const canDoAll = selectedServices.every(serviceId => servicesMap[serviceId]);
        if (canDoAll) validEmployees.push(employeeId);
      }

      const availableServicesOutput = new Set();
      for (let employeeId of validEmployees) {
        Object.keys(this.employeeToServicesMap[employeeId]).forEach(serviceId => {
          availableServicesOutput.add(parseInt(serviceId));
        });
      }

      return Array.from(availableServicesOutput);
    }
  }

  render(container) {
    const categories = this.data.categories || {};
    const categoryLinksHtml = [];
    const categoriesHtml = [];

    // Get the selected employee ID
    const employeeId = this.state.selectedEmployee;

    for (const catId in categories) {
      const cat = categories[catId];
      const catHash = '_' + this.simpleHash(cat.id.toString() + cat.name);
      if (!cat.services || !Object.keys(cat.services).length) continue;

      // Create a shallow copy of the category to avoid mutating the original data
      const catCopy = { ...cat };
      const servicesInCategory = Object.values(catCopy.services);

      // If in employees-first mode, filter the services for the selected employee
      if (this.employeesFirst && employeeId) {
        // Filter out services that the selected employee cannot perform
        const availableServicesInCategory = servicesInCategory.filter(service =>
            this.employeeToServicesMap[employeeId] && this.employeeToServicesMap[employeeId][service.id]
        );

        // Skip category if no available services for the selected employee
        if (availableServicesInCategory.length === 0) continue;

        // Replace services with the filtered available services
        catCopy.services = availableServicesInCategory.reduce((acc, service) => {
          acc[service.id] = service;
          return acc;
        }, {});
      } else if (isNonNullObject(this.subscription)) {
        // filter if we have subscription and type is single (one of the services from subscription)
        const availableServicesInCategory = servicesInCategory.filter(service =>
            this.subscription.service_ids.indexOf(service.id) !== -1
        );
        if (availableServicesInCategory.length === 0) continue;
        catCopy.services = availableServicesInCategory;
      }

      // Category nav link
      categoryLinksHtml.push(renderCategoryLink({ id: catHash, name: catCopy.name }));

      // Services list for the category
      const serviceListHtml = Object.values(catCopy.services).sort((a, b) => {
        // Compare by 'order' first, if available
        if (a.order !== undefined && b.order !== undefined) {
          if (a.order !== b.order) {
            return a.order - b.order;
          }
        } else if (a.order !== undefined) {
          return -1; // a has order, b doesn't — a comes first
        } else if (b.order !== undefined) {
          return 1;  // b has order, a doesn't — b comes first
        }

        // Fallback: compare by name
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      }).map(service => {
        // In employees-first mode, check if the selected employee can perform this service
        const isServiceAvailable = this.employeesFirst
            ? this.employeeToServicesMap[employeeId]?.[service.id]
            : true;

        // Only render the service if it's available
        if (!isServiceAvailable) return '';

        // When "employeesFirst" is enabled, we handle only availability, not max services here
        return renderServiceItem({
          service,
          isActive: this.selected.has(service.id),
          isDisabled: !this.isServiceEnabled(service.id), // This handles max services limit
          showDescription: this.data.config.hide_service_description !== 1,
          showTime: this.data.config.hide_service_time !== 1,
          showPrice: this.data.config.hide_service_prices !== 1
        });
      }).join('');

      // Category wrapper
      categoriesHtml.push(renderCategoryWrapper({
        id: catHash,
        name: catCopy.name,
        serviceListHtml,
        isMultiple: this.maxServices > 1
      }));
    }

    const html = renderCategoriesContainer({
      categoryLinksHtml: categoryLinksHtml.join(''),
      categoriesHtml: categoriesHtml.join(''),
      categoryCount: Object.keys(categories).length
    });

    container.html(html);
    this.bindEvents(container);
  }

  bindEvents(container) {
    const $ = this.$;

    $('.step-category-list-item', container).on('click', (e) => {
      const $item = $(e.currentTarget);
      const serviceId = parseInt($item.data('service-id'));

      if (this.selected.has(serviceId)) {
        this.selected.delete(serviceId);
        $item.removeClass('active');
      } else {
        if (this.maxServices === 1) {
          this.selected.clear();
          $('.step-category-list-item.active', container).removeClass('active');
        }
        this.selected.add(serviceId);
        $item.addClass('active');
      }

      this.refreshServiceStates(container);

      if (this.maxServices === 1 && this.selected.size === 1) {
        this.state.selectedServices = [...this.selected];
        this.next();
      } else {
        this.emit('services_footer', this.selected);
      }
    });

    // handlePageScroll(container);



    this.emit('services_footer', this.selected); // show if selected on init
  }

  handlePageScroll(container) {
  const $categoryTitles = jQuery(".step-category-title");
  const $sliderItems = jQuery(".step-multiple-categories-list li");
  // const $header = jQuery(".step-header-section");
  // const $headerTitle = jQuery(".step-header-title");
  // const $headerInfo = jQuery(".step-header-info");

  jQuery(window).on('scroll', function() {
    const $ = this.$;
    $('.step-multiple-categories-list', container).on('click', 'li a', function(event) {
      event.preventDefault();
      const targetId = $(this).attr('href').substring(1);
      const $target = $('#' + targetId);
      if ($target.length) {
        $('html, body').animate({
          scrollTop: $target.offset().top - 200
        }, 100, 'linear');
      }
    });
    sliderDebouncer();

    let viewportTop = jQuery(window).scrollTop();
    let viewportHeight = jQuery(document).height() - jQuery(window).height();
    let lastElement = null;

    for (let i = 0; i < $categoryTitles.length; i++) {
      let element = $categoryTitles[i];
      let elementTop = jQuery(element).offset().top | 0;
      if(elementTop <= viewportTop + 200)
      {
        lastElement = element;
      }
    }

    // New condition: if near the bottom, select the last category title
    if (viewportTop >= viewportHeight - 20) {
      lastElement = $categoryTitles[$categoryTitles.length - 1];
    }

    // If no element is found above the threshold, default to the first one
    if (lastElement == null) {
      lastElement = $categoryTitles[0];
    }

    if(lastElement) {
      let selectedId = lastElement.id;
      let $selectedItem = $sliderItems.find(`a[href="#${selectedId}"]`);

      $sliderItems.find("a.active").removeClass("active");
      $selectedItem.addClass("active");
    }
    /*
                        $categoryTitles.each(function(_, title) {
                            if (jQuery(title).isInViewport()) {
                                let selectedId = this.id;
                                let $selectedItem = $sliderItems.find(`a[href="#${selectedId}"]`);

                                $sliderItems.find("a.active").removeClass("active");
                                $selectedItem.addClass("active");
                            }
                        });
     */

  })
}

  refreshServiceStates(container) {
    const $ = this.$;
    this.availableServices = this.getAvailableServices([...this.selected]);

    $('.step-category-list-item', container).each((_, el) => {
      const $el = $(el);
      const serviceId = parseInt($el.data('service-id'));

      if (this.isServiceEnabled(serviceId)) {
        $el.removeClass('disabled');
        $el.removeAttr('data-disabled');
      } else {
        $el.addClass('disabled');
        $el.attr("data-disabled", "true");
      }
    });
  }

  isServiceEnabled(serviceId) {
    const maxReached = this.selected.size >= this.maxServices;
    if (this.selected.size === 0 || this.selected.has(serviceId)) return true;
    if (maxReached) return false;

    if (this.employeesFirst) {
      // In "employees-first" mode, all services are available once an employee is selected
      return true;
    }

    return this.availableServices.includes(serviceId);
  }

  next() {
    if (this.selected.size > 0) {
      this.emit('services_selected', [...this.selected]);
    }
  }

  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36); // Convert to base-36 for shorter hash
  }
};
